/**
 * Configuration → Business Opportunities → Prompt — admins only.
 *
 * Every version of what business-opportunity research sends the model: the
 * system and user prompt templates (Jinja2), the answer schema, and the
 * dimensions they carry. Versions are append-only: editing the prompt, a
 * dimensions save that changes what the prompt carries, or an answer schema
 * changed in code makes a new one. Research
 * always runs with the latest; each run records the version it used, and an
 * idea's page links back here (`&version=<n>`).
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye, FileText, Pencil } from 'lucide-react';

import { usePromptVersion, usePromptVersions } from '@/api/queries';
import type { PromptVersion } from '@/api/types';
import { formatDateTime } from '@/ui/format';
import { Modal } from '@/ui/Modal';
import { SectionActions } from '@/ui/SectionActions';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';
import { useSession } from '@/session/SessionContext';
import { PromptEditDialog } from './PromptEditDialog';

type Part = 'system' | 'user' | 'schema' | 'dimensions';

const PARTS: { key: Part; label: string; note: string }[] = [
  { key: 'system', label: 'System prompt', note: 'The instructions, the same for every idea. Jinja2; the variables are filled in per run.' },
  { key: 'user', label: 'User message', note: 'One per idea. Jinja2; the idea’s own text is filled in per run.' },
  { key: 'schema', label: 'Answer schema', note: 'The JSON shape the model must answer in. Built from the dimensions; the industry list is filled in per run.' },
  { key: 'dimensions', label: 'Dimensions', note: 'The dimensions and weights this version carries.' },
];

function VersionDialog({ version, onClose }: { version: number; onClose: () => void }) {
  const detail = usePromptVersion(version);
  const [part, setPart] = useState<Part>('system');
  const current = PARTS.find((p) => p.key === part)!;

  let body;
  if (detail.isPending) body = <LoadingCard label="Loading the prompt version" />;
  else if (detail.error) body = <LoadFailure error={detail.error} onRetry={() => void detail.refetch()} />;
  else {
    const d = detail.data;
    body = (
      <>
        <div className="tabs prompt-tabs" role="tablist" aria-label="Prompt parts">
          {PARTS.map((p) => (
            <button
              key={p.key}
              type="button"
              role="tab"
              id={`version-tab-${p.key}`}
              aria-selected={part === p.key}
              aria-controls="version-panel"
              className={`tab${part === p.key ? ' is-active' : ''}`}
              onClick={() => setPart(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div id="version-panel" role="tabpanel" aria-labelledby={`version-tab-${part}`} className="stack-sm">
          <p className="hint">{current.note}</p>
          {part === 'dimensions' ? (
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th scope="col">Dimension</th>
                    <th scope="col">Question for the AI</th>
                    <th scope="col" className="num">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {d.dimensions.map((dim) => (
                    <tr key={dim.key}>
                      <td><b>{dim.label}</b></td>
                      <td>{dim.description}</td>
                      <td className="num">{dim.weight}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <pre className="text-preview prompt-preview" tabIndex={0}>
              {part === 'system' ? d.system_template : part === 'user' ? d.user_template : JSON.stringify(d.answer_schema, null, 2)}
            </pre>
          )}
        </div>
      </>
    );
  }

  return (
    <Modal
      title={`Prompt v${version}`}
      subtitle={detail.data ? `${detail.data.note} · ${formatDateTime(detail.data.created_at)}${detail.data.created_by ? ` · ${detail.data.created_by.display_name}` : ''}` : undefined}
      size="wide"
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          Close
        </button>
      }
    >
      {body}
    </Modal>
  );
}

/** The version open in the dialog lives in `?version=`, so an idea's page can link to it. */
function useOpenedVersion() {
  const [params, setParams] = useSearchParams();
  const opened = Number(params.get('version')) || null;
  const open = (version: number | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('part', 'prompt');
        if (version === null) next.delete('version');
        else next.set('version', String(version));
        return next;
      },
      { replace: true },
    );
  return { opened, open };
}

function VersionRow({ v, onOpen }: { v: PromptVersion; onOpen: (version: number) => void }) {
  return (
    <tr>
      <td className="nowrap">
        <span className="row process-name">
          <FileText aria-hidden="true" />
          <b className="mono">v{v.version}</b>
          {v.is_current && <span className="badge badge-info">Current</span>}
        </span>
      </td>
      <td>{v.note}</td>
      <td>{v.created_by?.display_name ?? <span className="hint">System</span>}</td>
      <td className="nowrap">{formatDateTime(v.created_at)}</td>
      <td className="num">{v.run_count}</td>
      <td className="process-prompt">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => onOpen(v.version)}>
          <Eye aria-hidden="true" /> View
        </button>
      </td>
    </tr>
  );
}

function VersionsTable({ versions, onOpen }: { versions: PromptVersion[]; onOpen: (version: number) => void }) {
  return (
    <div className="card">
      <div className="table-scroll">
        <table className="data">
          <thead>
            <tr>
              <th scope="col">Version</th>
              <th scope="col">Change</th>
              <th scope="col">By</th>
              <th scope="col">Created</th>
              <th scope="col" className="num">Research runs</th>
              <th scope="col"><span className="sr-only">Open</span></th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <VersionRow key={v.id} v={v} onOpen={onOpen} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PromptVersionsSection() {
  const versions = usePromptVersions();
  const { can } = useSession();
  const latest = versions.data?.versions[0]?.version ?? null;
  const currentDetail = usePromptVersion(latest);
  const [editing, setEditing] = useState(false);
  const { opened, open } = useOpenedVersion();

  if (versions.isPending) return <LoadingCard label="Loading prompt versions" />;
  if (versions.error) return <LoadFailure error={versions.error} onRetry={() => void versions.refetch()} />;

  return (
    <>
      {can('research.prompt.manage') && (
        <SectionActions>
          <button type="button" className="btn btn-secondary" disabled={!currentDetail.data} onClick={() => setEditing(true)}>
            <Pencil aria-hidden="true" /> Edit prompt
          </button>
        </SectionActions>
      )}

      <VersionsTable versions={versions.data.versions} onOpen={open} />

      {opened !== null && <VersionDialog version={opened} onClose={() => open(null)} />}
      {editing && currentDetail.data && <PromptEditDialog current={currentDetail.data} onClose={() => setEditing(false)} />}
    </>
  );
}
